// ---------------------------------------------------------------------------
// Client replica — mirrors the server's redacted view and layers on
// presentation state: position/rotation tweening, flip animation, fx timers,
// and local prediction for the object you're holding.
// ---------------------------------------------------------------------------

import { lerp, lerpAngle, type V3 } from "../shared/math";
import { registerDynamicDefs } from "../shared/dynamicDefs";
import type { PublicObj, ServerMsg } from "../shared/protocol";
import { TABLE, type Player } from "../shared/state";

export interface RenderObj extends PublicObj {
  /** Rendered (tweened) position/rotation — what the renderers draw. */
  rp: V3;
  ryaw: number;
  /** Flip animation progress: 1 = face up, 0 = face down. */
  flipT: number;
  /** Spawn pop-in progress 0→1. */
  spawnT: number;
  /** Time remaining on shuffle jiggle fx. */
  fxShuffle: number;
  /** Time remaining on dice tumble fx (value is scrambled while > 0). */
  fxRoll: number;
}

export interface CursorState {
  x: number;
  z: number;
  rx: number;
  rz: number;
  seen: number;
}

export interface LogLine {
  m: string;
  at: number;
  /** Chat lines carry the speaker; plain event lines don't. */
  who?: string;
  color?: string;
}

export class Replica {
  objs = new Map<string, RenderObj>();
  players: Player[] = [];
  cursors = new Map<string, CursorState>();
  log: LogLine[] = [];
  you = "";
  seat = -1;
  roomId = "";
  /** Table dimensions + type — sent in welcome; renderers size from this. */
  table: { w: number; d: number; type: string } = { w: TABLE.w, d: TABLE.d, type: "blank" };
  /** Object ids currently held & predicted locally (input maintains this). */
  localHolds = new Set<string>();
  /** Multi-select (client-side only; input maintains, renderers highlight). */
  selected = new Set<string>();
  /** While dragging: the object the drop would merge into (highlighted). */
  dropTargetId: string | null = null;
  /** While dragging with no merge target: footprint ghost at the drop spot. */
  dropGhost: { x: number; z: number; yaw: number; w: number; d: number; cyl: boolean } | null = null;
  /** Sound hook — fired on observed state changes (flips, drops, stacking).
   *  `def` is the object's def id so the effect can vary by material. */
  onSfx: ((kind: "flip" | "drop" | "stack" | "draw", def: string) => void) | null = null;
  private now = 0;

  get self(): Player | undefined {
    return this.players.find((p) => p.id === this.you);
  }

  apply(msg: ServerMsg): void {
    switch (msg.t) {
      case "welcome": {
        registerDynamicDefs(msg.defs ?? []);
        this.you = msg.you;
        this.seat = msg.seat;
        this.roomId = msg.roomId;
        this.players = msg.players;
        if (msg.table) this.table = { ...msg.table };
        this.objs.clear();
        for (const o of msg.objs) this.upsert(o, true);
        break;
      }
      case "players":
        this.players = msg.players;
        break;
      case "upsert":
        for (const o of msg.objs) this.upsert(o, false);
        break;
      case "remove":
        for (const id of msg.ids) {
          this.objs.delete(id);
          this.selected.delete(id);
          this.localHolds.delete(id);
        }
        break;
      case "pos":
        for (const [id, x, y, z, yaw] of msg.o) {
          const o = this.objs.get(id);
          if (!o || this.localHolds.has(id)) continue;
          o.pos = { x, y, z };
          o.yaw = yaw;
        }
        break;
      case "cursors":
        for (const [pid, x, z] of msg.c) {
          if (pid === this.you) continue;
          const c = this.cursors.get(pid);
          if (c) {
            c.x = x; c.z = z; c.seen = this.now;
          } else {
            this.cursors.set(pid, { x, z, rx: x, rz: z, seen: this.now });
          }
        }
        break;
      case "log":
        this.log.push({ m: msg.m, at: performance.now() });
        if (this.log.length > 60) this.log.shift();
        break;
      case "chat":
        this.log.push({ m: msg.m, who: msg.who, color: msg.color, at: performance.now() });
        if (this.log.length > 60) this.log.shift();
        break;
      case "fx": {
        const o = this.objs.get(msg.id);
        if (o && msg.kind === "shuffle") o.fxShuffle = 0.6;
        if (o && msg.kind === "roll") o.fxRoll = 0.9;
        break;
      }
      case "defs":
        registerDynamicDefs(msg.defs);
        break;
      case "error":
        this.log.push({ m: `⚠ ${msg.m}`, at: performance.now() });
        break;
    }
  }

  private upsert(pub: PublicObj, snap: boolean): void {
    const prev = this.objs.get(pub.id);
    if (prev) {
      // Observed transitions drive sounds (everyone at the table hears them).
      if (this.onSfx) {
        if (prev.faceUp !== pub.faceUp) this.onSfx("flip", pub.def);
        else if ((pub.count ?? 0) > (prev.count ?? 0)) this.onSfx("stack", pub.def);
        else if (prev.heldBy && !pub.heldBy) this.onSfx("drop", pub.def);
      }
      const keepLocalPos = this.localHolds.has(pub.id);
      const localX = prev.pos.x, localZ = prev.pos.z;
      Object.assign(prev, pub);
      prev.pos = { ...pub.pos };
      if (keepLocalPos) {
        // We're dragging it — trust our own prediction for x/z, adopt server y.
        prev.pos.x = localX;
        prev.pos.z = localZ;
      }
    } else {
      // A card materializing straight into a hand zone = a draw/deal swish.
      if (!snap && pub.inZone && this.onSfx) this.onSfx("draw", pub.def);
      const o: RenderObj = {
        ...pub,
        rp: { ...pub.pos },
        ryaw: pub.yaw,
        flipT: pub.faceUp ? 1 : 0,
        spawnT: snap ? 1 : 0,
        fxShuffle: 0,
        fxRoll: 0,
      };
      this.objs.set(pub.id, o);
    }
  }

  /** Advance animations. Call once per frame. */
  tick(dt: number): void {
    this.now += dt;
    const posK = 1 - Math.exp(-dt * 14);
    const fastK = 1 - Math.exp(-dt * 26);
    for (const o of this.objs.values()) {
      const k = this.localHolds.has(o.id) ? fastK : posK;
      o.rp.x = lerp(o.rp.x, o.pos.x, k);
      o.rp.y = lerp(o.rp.y, o.pos.y, k);
      o.rp.z = lerp(o.rp.z, o.pos.z, k);
      o.ryaw = lerpAngle(o.ryaw, o.yaw, k);
      const targetFlip = o.faceUp ? 1 : 0;
      const df = targetFlip - o.flipT;
      const step = dt * 5.5;
      o.flipT = Math.abs(df) <= step ? targetFlip : o.flipT + Math.sign(df) * step;
      o.spawnT = Math.min(1, o.spawnT + dt * 4);
      o.fxShuffle = Math.max(0, o.fxShuffle - dt);
      o.fxRoll = Math.max(0, o.fxRoll - dt);
    }
    for (const c of this.cursors.values()) {
      const k = 1 - Math.exp(-dt * 12);
      c.rx = lerp(c.rx, c.x, k);
      c.rz = lerp(c.rz, c.z, k);
    }
    // Drop cursors of players no longer present/connected.
    for (const [pid] of this.cursors) {
      const p = this.players.find((q) => q.id === pid);
      if (!p || !p.connected) this.cursors.delete(pid);
    }
  }

  /** Objects sorted bottom-to-top for painter's-algorithm rendering. */
  drawOrder(): RenderObj[] {
    return [...this.objs.values()].sort((a, b) => a.rp.y - b.rp.y || a.id.localeCompare(b.id));
  }
}
